import { useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { SERVICE_SUBNAV_ITEMS, serviceSubNavActiveIndex } from "@/config/serviceSubNavRoutes";
import { useBreadcrumbJsonLd } from "@/hooks/useBreadcrumbJsonLd";
import { getSiteOriginRuntime } from "@/config/siteBrand";

function absoluteFromSiteRoot(relativeHref) {
  const origin = getSiteOriginRuntime();
  const base = origin.endsWith("/") ? origin : `${origin}/`;
  return new URL(relativeHref, base).href;
}

export default function ServiceHomeBreadcrumb({ t, lang, pathname }) {
  const { breadcrumbItems, currentLabel, homeHref } = useMemo(() => {
    const idx = serviceSubNavActiveIndex(pathname);
    const current = idx >= 0 ? SERVICE_SUBNAV_ITEMS[idx] : null;
    if (!current) return { breadcrumbItems: [], currentLabel: "", homeHref: "/" };

    const homeHref = lang === "ro" ? "/" : `/?lang=${lang}`;
    const pageHref =
      lang === "ro" ? current.path : `${current.path}?lang=${lang}`;

    return {
      breadcrumbItems: [
        { name: t.nav.home, url: absoluteFromSiteRoot(homeHref) },
        { name: t.nav[current.navKey], url: absoluteFromSiteRoot(pageHref) },
      ],
      currentLabel: t.nav[current.navKey],
      homeHref,
    };
  }, [lang, pathname, t]);

  useBreadcrumbJsonLd(breadcrumbItems, "service-home-breadcrumb");

  if (breadcrumbItems.length < 2 || !currentLabel) return null;

  return (
    <div className="border-b border-[#e2e6ec] bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav aria-label={t.nav.breadcrumbAria} className="text-sm text-slate-500">
          <ol className="flex flex-wrap items-center gap-1">
            <li className="min-w-0">
              <a
                href={homeHref}
                className="text-[#0b378c] hover:text-[#e3191b] underline-offset-2 hover:underline"
              >
                {t.nav.home}
              </a>
            </li>
            <li className="flex shrink-0 items-center gap-1" aria-hidden="true">
              <ChevronRight className="h-4 w-4 text-slate-400" strokeWidth={2} />
            </li>
            <li className="min-w-0 font-medium text-[#0b1220]" aria-current="page">
              {currentLabel}
            </li>
          </ol>
        </nav>
      </div>
    </div>
  );
}
